import { NOTE_MIN_HEIGHT, type NoteBlockModel } from '@blocksuite/affine-model';
import { EDGELESS_BLOCK_CHILD_PADDING } from '@blocksuite/affine-shared/consts';
import { almostEqual, Bound, isSerializedXYWH } from '@blocksuite/global/gfx';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { type EditorHost, ShadowlessElement } from '@blocksuite/std';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { resolveEdgelessNoteStyle } from '../edgeless-note-style';
import { ACTIVE_NOTE_EXTRA_PADDING } from '../note-edgeless-block.css';
import { hasPendingEmbeddedNoteLayout } from '../note-layout-readiness';

const NOTE_HEIGHT_PERSIST_EPSILON = 2;
const NOTE_HEIGHT_PERSIST_INTENT_WINDOW_MS = 5000;
const NOTE_HEIGHT_PERSIST_INTENT_EVENTS = [
  'beforeinput',
  'compositionend',
  'drop',
  'input',
  'keydown',
  'paste',
] as const;

export class EdgelessNoteMask extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  private _lastNoteHeightPersistIntentAt = 0;

  private readonly _markNoteHeightPersistIntent = (event: Event) => {
    if (!event.isTrusted) {
      return;
    }

    this._lastNoteHeightPersistIntentAt = Date.now();
  };

  private _hasRecentNoteHeightPersistIntent() {
    return (
      Date.now() - this._lastNoteHeightPersistIntentAt <
      NOTE_HEIGHT_PERSIST_INTENT_WINDOW_MS
    );
  }

  private _observeNoteHeightPersistIntent(target: HTMLElement | null) {
    if (!target) {
      return;
    }

    for (const eventName of NOTE_HEIGHT_PERSIST_INTENT_EVENTS) {
      target.addEventListener(eventName, this._markNoteHeightPersistIntent, {
        capture: true,
        passive: true,
      });
    }
    this._disposables.add(() => {
      for (const eventName of NOTE_HEIGHT_PERSIST_INTENT_EVENTS) {
        target.removeEventListener(
          eventName,
          this._markNoteHeightPersistIntent,
          {
            capture: true,
          }
        );
      }
    });
  }

  protected override firstUpdated() {
    this._observeNoteHeightPersistIntent(this.parentElement);
    const contentDOM = this.parentElement?.querySelector<HTMLElement>(
      '.edgeless-note-page-content'
    );
    if (!contentDOM) {
      return;
    }

    const observer = new ResizeObserver(entries => {
      const canPersistHeight =
        !this.model.store.readonly &&
        this.editing &&
        this._hasRecentNoteHeightPersistIntent();
      for (const entry of entries) {
        if (hasPendingEmbeddedNoteLayout(contentDOM)) {
          return;
        }

        const edgeless = this.model.props.edgeless;
        if (!edgeless?.collapse) {
          const persistedXYWH = this.model.yBlock.get('prop:xywh');
          if (!isSerializedXYWH(persistedXYWH)) {
            return;
          }

          const bound = Bound.deserialize(persistedXYWH);
          const scale = edgeless?.scale ?? 1;
          const height = Math.max(
            (entry.contentRect.height + 2 * EDGELESS_BLOCK_CHILD_PADDING) *
              scale,
            NOTE_MIN_HEIGHT * scale
          );

          if (
            !height ||
            almostEqual(bound.h, height, NOTE_HEIGHT_PERSIST_EPSILON) ||
            !canPersistHeight
          ) {
            return;
          }

          bound.h = height;
          this.model.stash('xywh');
          this.model.xywh = bound.serialize();
          this.model.pop('xywh');
        }
      }
    });

    // The mask is sized from the note owner. Observing it and feeding that
    // measurement back into the owner creates a positive resize loop while
    // editing adds ACTIVE_NOTE_EXTRA_PADDING. Content is the intrinsic height
    // authority and cannot grow as a consequence of the owner's own height.
    observer.observe(contentDOM);

    this._disposables.add(() => {
      observer.disconnect();
    });
  }

  override render() {
    const extra = this.editing ? ACTIVE_NOTE_EXTRA_PADDING : 0;
    const { borderRadius } = resolveEdgelessNoteStyle(
      this.model.props.edgeless
    );

    return html`
      <div
        class="affine-note-mask"
        style=${styleMap({
          position: 'absolute',
          top: `${-extra}px`,
          left: `${-extra}px`,
          bottom: `${-extra}px`,
          right: `${-extra}px`,
          zIndex: '1',
          pointerEvents: this.editing || this.disableMask ? 'none' : 'auto',
          borderRadius: `${borderRadius * this.zoom}px`,
        })}
      ></div>
    `;
  }

  @property({ attribute: false })
  accessor disableMask!: boolean;

  @property({ attribute: false })
  accessor editing!: boolean;

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor model!: NoteBlockModel;

  @property({ attribute: false })
  accessor zoom!: number;
}

declare global {
  interface HTMLElementTagNameMap {
    'edgeless-note-mask': EdgelessNoteMask;
  }
}
